import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SearchBar } from '@/components/SearchBar';
import { MapGrid } from '@/components/MapGrid';
import { api } from '@/lib/api';

export function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';

  const {
    data: results,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['search', query],
    queryFn: ({ signal }) => api.searchMaps(query, 50, { signal }),
    enabled: !!query,
  });

  const handleSearch = (newQuery: string) => {
    navigate(`/search?q=${encodeURIComponent(newQuery)}`);
  };

  const renderStatusMessage = () => {
    if (!query) {
      return null;
    }

    if (isLoading) {
      return 'Searching...';
    }

    if (isError) {
      return error instanceof Error
        ? `Search failed: ${error.message}`
        : 'Search failed. Please try again.';
    }

    if (results && results.length > 0) {
      return `Found ${results.length} result${results.length !== 1 ? 's' : ''} for "${query}"`;
    }

    return `No results found for "${query}"`;
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-5xl font-display font-bold text-stone-100 mb-6 tracking-wide">Search Maps</h1>
        <SearchBar
          onSearch={handleSearch}
          placeholder="Search by title or description..."
          initialQuery={query}
        />
      </div>

      {query && (
        <div>
          <p className="text-stone-300 mb-4">{renderStatusMessage()}</p>

          {!isError && <MapGrid maps={results || []} isLoading={isLoading} />}

          {isError && (
            <div className="rounded-md border border-blood-700 bg-blood-900/40 p-4 text-blood-300">
              {error instanceof Error
                ? error.message
                : 'Unable to load maps right now. Please try again later.'}
            </div>
          )}
        </div>
      )}

      {!query && (
        <div className="text-center py-12 text-stone-400">
          <svg
            className="mx-auto h-12 w-12 text-stone-600 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <p>Enter a search term to find maps</p>
        </div>
      )}
    </div>
  );
}
